import Loader from 'app/custom/loader/Loader'
import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'
import {useAppDispatch} from '../../../redux/hooks'
import {getEvent, submitEvent} from '../../../redux/Slice/adminSlice'
import {showErrorMessage, showSuccessMessage} from '_metronic/helpers/swalHelper'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/bootstrap.css'
import ThankYou from './ThankYou'

interface Guest {
  first_name: string
  last_name: string
}

interface Answers {
  [questionId: number]: string | number | number[]
}

interface Errors {
  first_name?: string
  last_name?: string
  email?: string
  contact_number?: string
  guests?: Record<number, {first_name?: string; last_name?: string}>
  questions?: Record<number, string>
}

const EventParticipation = () => {
  const {t} = useTranslation()
  const {id = ''} = useParams()
  const dispatch = useAppDispatch()
  const [loader, setLoader] = useState<boolean>(true)
  const [event, setEvent] = useState<any>(null)
  const [firstName, setFirstName] = useState<string>('')
  const [contactNumber, setContactNumber] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [guests, setGuests] = useState<Guest[]>([])
  const [answers, setAnswers] = useState<Answers>({})
  const [errors, setErrors] = useState<Errors>({})
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [comments, setComments] = useState<string>('')
  const DEFAULT_PHONE_COUNTRY = 'ch'

  useEffect(() => {
    dispatch(getEvent(id)).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') setEvent(res.payload)
      setLoader(false)
    })
  }, [])

  const addGuest = () => {
    if (guests.length >= Number(event?.guest_number ?? 0)) return
    setGuests((prev) => [
      ...prev,
      {
        first_name: '',
        last_name: '',
      },
    ])
  }

  const removeGuest = (index: number) => {
    setGuests((prev) => prev.filter((_, i) => i !== index))
    setErrors((prev) => {
      const guestErrors = {...(prev.guests ?? {})}
      delete guestErrors[index]
      return {
        ...prev,
        guests: guestErrors,
      }
    })
  }

  const updateGuest = (index: number, field: keyof Guest, value: string) => {
    setGuests((prev) =>
      prev.map((guest, i) =>
        i === index
          ? {
              ...guest,
              [field]: value,
            }
          : guest
      )
    )

    setErrors((prev) => ({
      ...prev,
      guests: {
        ...(prev.guests ?? {}),
        [index]: {
          ...(prev.guests?.[index] ?? {}),
          [field]: undefined,
        },
      },
    }))
  }

  const handleInputAnswer = (questionId: number, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }))
    setErrors((prev) => ({
      ...prev,
      questions: {
        ...(prev.questions ?? {}),
        [questionId]: '',
      },
    }))
  }

  const handleRadioAnswer = (questionId: number, optionId: number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: optionId,
    }))
    setErrors((prev) => ({
      ...prev,
      questions: {
        ...(prev.questions ?? {}),
        [questionId]: '',
      },
    }))
  }

  const handleCheckboxAnswer = (questionId: number, optionId: number, checked: boolean) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[questionId]) ? (prev[questionId] as number[]) : []
      return {
        ...prev,
        [questionId]: checked
          ? [...current, optionId]
          : current.filter((item) => item !== optionId),
      }
    })
    setErrors((prev) => ({
      ...prev,
      questions: {
        ...(prev.questions ?? {}),
        [questionId]: '',
      },
    }))
  }

  const validateForm = () => {
    const validationErrors: Errors = {}
    const guestErrors: Record<number, {first_name?: string; last_name?: string}> = {}
    const questionErrors: Record<number, string> = {}

    if (!firstName.trim())
      validationErrors.first_name = t('VALIDATION.REQUIRED_', {name: t('LABEL.FIRST_NAME')})

    if (!lastName.trim())
      validationErrors.last_name = t('VALIDATION.REQUIRED_', {name: t('LABEL.LAST_NAME')})

    if (!email.trim())
      validationErrors.email = t('VALIDATION.REQUIRED_', {name: t('PROFILE.EMAIL')})
    else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailPattern.test(email.trim())) validationErrors.email = t('VALIDATION.INVALID_EMAIL')
    }
    if (!contactNumber.trim()) {
      validationErrors.contact_number = t('VALIDATION.REQUIRED_', {
        name: t('LABEL.CONTACT_NUMBER'),
      })
    } else {
      const phoneDigits = contactNumber.replace(/\D/g, '')

      if (phoneDigits.length < 7 || phoneDigits.length > 15) {
        validationErrors.contact_number = t('VALIDATION.INVALID_PHONE')
      }
    }

    guests.forEach((guest, index) => {
      const currentErrors: {
        first_name?: string
        last_name?: string
      } = {}

      if (!guest.first_name.trim())
        currentErrors.first_name = t('VALIDATION.REQUIRED_', {name: t('LABEL.FIRST_NAME')})

      if (!guest.last_name.trim())
        currentErrors.last_name = t('VALIDATION.REQUIRED_', {name: t('LABEL.LAST_NAME')})

      if (Object.keys(currentErrors).length > 0) guestErrors[index] = currentErrors
    })

    if (Object.keys(guestErrors).length > 0) validationErrors.guests = guestErrors

    event.questions?.forEach((question: any) => {
      if (!question.required) return
      const answer = answers[question.id]
      if (question.type === 'checkbox') {
        if (!Array.isArray(answer) || answer.length === 0)
          questionErrors[question.id] = t('VALIDATION.REQUIRED_', {name: t('EVENT.QUESTION')})

        return
      }

      if (answer === undefined || answer === null || String(answer).trim() === '')
        questionErrors[question.id] = t('VALIDATION.REQUIRED_', {name: t('EVENT.QUESTION')})
    })

    if (Object.keys(questionErrors).length > 0) validationErrors.questions = questionErrors
    setErrors(validationErrors)
    return Object.keys(validationErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm() || submitting) return

    setSubmitting(true)

    const data = {
      event_id: event.id,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      contact_number: contactNumber,
      guests: guests.map((guest) => ({
        first_name: guest.first_name.trim(),
        last_name: guest.last_name.trim(),
      })),
      answers,
      comments: comments.trim(),
    }

    try {
      const res = await dispatch(submitEvent(data))
      if (res.meta.requestStatus === 'fulfilled') {
        showSuccessMessage(t('MESSAGES.SUBMITTED'))
        setIsSuccess(true)
      } else showErrorMessage(res.payload?.message ?? t('MESSAGES.SERVER_ERROR'))
    } catch (error) {
      showErrorMessage(t('MESSAGES.SERVER_ERROR'))
    } finally {
      setSubmitting(false)
    }
  }

  if (isSuccess) return <ThankYou />
  if (loader) return <Loader />

  if (event === 'EVENT.EXPIRED' || event === 'EVENT.MAX_REACHED') {
    return (
      <div className='container'>
        <div className='bg-white rounded shadow-sm mb-3 p-4'>
          <div className='d-flex align-items-center'>
            <img
              src='/media/logo/horizon-logo.png'
              alt='Horizon Genève'
              style={{
                width: '200px',
                height: '90px',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
        <div className='card border-0 shadow-sm'>
          <div className='card-body text-center py-10'>
            <span className='fa fa-circle-exclamation text-info mb-4' style={{fontSize: '45px'}} />
            <h4 className='fw-bolder text-primary'>{t(event)}</h4>
          </div>
        </div>
      </div>
    )
  }

  const isOnline = String(event.event_type).toLowerCase() === 'online'

  return (
    <div className='container'>
      <div className='bg-white rounded shadow-sm mb-3 p-4'>
        <div className='d-flex align-items-center'>
          <img
            src='/media/logo/horizon-logo.png'
            alt='Horizon Genève'
            style={{
              width: '200px',
              height: '90px',
              objectFit: 'contain',
            }}
          />
        </div>
      </div>

      <div className='card border-0 shadow-sm'>
        <div className='card-header border-0 bg-primary' style={{minHeight: '40px'}}>
          <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between w-100 gap-3'>
            <h3 className='text-white fw-bolder mb-0'>{event.title}</h3>
            {event.date && (
              <div className='d-flex align-items-center text-white'>
                <span className='fa fa-calendar-alt mx-2' />
                <span className='fs-6 fw-bold'>{event.date}</span>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className='card-body p-5 p-md-8'>
            {event.link && (
              <div className='rounded border bg-light p-4 mb-7'>
                <div className='d-flex align-items-center'>
                  <div className='symbol symbol-40px mx-3 flex-shrink-0'>
                    <span className='symbol-label bg-light-info'>
                      <span className={`fa fa-${isOnline ? 'link' : 'location-dot'} text-info`} />
                    </span>
                  </div>

                  <div className='overflow-hidden'>
                    {isOnline ? (
                      <a
                        href={event.link}
                        target='_blank'
                        rel='noreferrer'
                        className='fw-bold fs-7 text-break'
                      >
                        {event.link}
                      </a>
                    ) : (
                      <div className='fw-bold fs-7'>{event.link}</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Description */}
            {event.content && (
              <div className='mb-8'>
                <h5 className='fw-bolder mb-3'>
                  <span className='fa fa-circle-info text-primary mx-2' />
                  {t('EVENT.DESCRIPTION')}
                </h5>
                <div
                  className='text-gray-700 fs-7'
                  dangerouslySetInnerHTML={{
                    __html: event.content,
                  }}
                />
              </div>
            )}

            <div className='separator mb-7' />

            {/* Participant */}
            <div className='mb-5'>
              <div className='d-flex align-items-center mb-5'>
                <div className='symbol symbol-35px mx-3'>
                  <span className='symbol-label bg-light-primary'>
                    <span className='fa fa-user text-primary' />
                  </span>
                </div>
                <h4 className='fw-bolder mb-0'>{t('EVENT.PARTICIPANT_INFORMATION')}</h4>
              </div>

              <div className='row g-4'>
                <div className='col-md-3'>
                  <label className='form-label required fw-bold'>{t('LABEL.FIRST_NAME')}</label>

                  <input
                    type='text'
                    className={`form-control form-control-sm text-capitalize ${
                      errors.first_name ? 'is-invalid' : ''
                    }`}
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value)
                      setErrors((prev) => ({
                        ...prev,
                        first_name: undefined,
                      }))
                    }}
                  />
                  {errors.first_name && <div className='invalid-feedback'>{errors.first_name}</div>}
                </div>

                <div className='col-md-3'>
                  <label className='form-label required fw-bold'>{t('LABEL.LAST_NAME')}</label>
                  <input
                    type='text'
                    className={`form-control form-control-sm text-uppercase ${
                      errors.last_name ? 'is-invalid' : ''
                    }`}
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value)
                      setErrors((prev) => ({
                        ...prev,
                        last_name: undefined,
                      }))
                    }}
                  />

                  {errors.last_name && <div className='invalid-feedback'>{errors.last_name}</div>}
                </div>

                <div className='col-md-3'>
                  <label className='form-label required fw-bold'>{t('PROFILE.EMAIL')}</label>
                  <input
                    type='email'
                    className={`form-control form-control-sm ${errors.email ? 'is-invalid' : ''}`}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)

                      setErrors((prev) => ({
                        ...prev,
                        email: undefined,
                      }))
                    }}
                  />

                  {errors.email && <div className='invalid-feedback'>{errors.email}</div>}
                </div>
                <div className='col-md-3'>
                  <label className='form-label fw-bold required'>{t('LABEL.CONTACT_NUMBER')}</label>

                  <PhoneInput
                    country={DEFAULT_PHONE_COUNTRY}
                    value={contactNumber}
                    preferredCountries={['ch', 'fr', 'sn']}
                    enableSearch
                    searchPlaceholder={t('LABEL.SEARCH_COUNTRY')}
                    countryCodeEditable={false}
                    inputProps={{
                      name: 'contact_number',
                      required: true,
                      autoComplete: 'tel',
                    }}
                    onChange={(phone) => {
                      setContactNumber(phone)

                      setErrors((prev) => ({
                        ...prev,
                        contact_number: undefined,
                      }))
                    }}
                    inputClass={`form-control form-control-sm ${
                      errors.contact_number ? 'is-invalid' : ''
                    }`}
                    containerClass='w-100'
                    inputStyle={{
                      width: '100%',
                      height: '38px',
                    }}
                  />

                  {errors.contact_number && (
                    <div className='text-danger fs-8 mt-1'>{errors.contact_number}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Guests */}
            {Number(event.guest_number) > 0 && (
              <>
                <div className='separator mb-7' />
                <div className='mb-5'>
                  <div className='d-flex justify-content-between align-items-center mb-4'>
                    <div className='d-flex align-items-center'>
                      <div className='symbol symbol-35px mx-3'>
                        <span className='symbol-label bg-light-info'>
                          <span className='fa fa-user-plus text-info' />
                        </span>
                      </div>

                      <div>
                        <h5 className='fw-bolder mb-0'>{t('EVENT.INVITE_GUESTS')}</h5>
                        <span className='text-muted fs-7'>
                          {guests.length} / {event.guest_number}
                        </span>
                      </div>
                    </div>

                    <button
                      type='button'
                      className='btn btn-sm btn-light-primary fw-bold'
                      onClick={addGuest}
                      disabled={guests.length >= Number(event.guest_number)}
                    >
                      <span className='fa fa-plus mx-1' />
                      {t('EVENT.ADD_GUEST')}
                    </button>
                  </div>

                  {guests.length > 0 && (
                    <div className='border rounded'>
                      {guests.map((guest, index) => (
                        <div
                          key={index}
                          className={`p-4 ${index !== guests.length - 1 ? 'border-bottom' : ''}`}
                        >
                          <div className='row g-3'>
                            <div className='col-auto'>
                              <label className='form-label fw-bold invisible'>#</label>
                              <div
                                className='d-flex align-items-center justify-content-center rounded-circle bg-light-primary text-primary fw-bolder'
                                style={{
                                  width: '32px',
                                  height: '32px',
                                }}
                              >
                                {index + 1}
                              </div>
                            </div>

                            <div className='col'>
                              <label className='form-label required fw-bold'>
                                {t('LABEL.FIRST_NAME')}
                              </label>

                              <input
                                type='text'
                                className={`form-control form-control-sm text-capitalize ${
                                  errors.guests?.[index]?.first_name ? 'is-invalid' : ''
                                }`}
                                value={guest.first_name}
                                onChange={(e) => updateGuest(index, 'first_name', e.target.value)}
                              />

                              {errors.guests?.[index]?.first_name && (
                                <div className='invalid-feedback'>
                                  {errors.guests[index].first_name}
                                </div>
                              )}
                            </div>

                            <div className='col'>
                              <label className='form-label required fw-bold'>
                                {t('LABEL.LAST_NAME')}
                              </label>
                              <input
                                type='text'
                                className={`form-control form-control-sm text-uppercase ${
                                  errors.guests?.[index]?.last_name ? 'is-invalid' : ''
                                }`}
                                value={guest.last_name}
                                onChange={(e) => updateGuest(index, 'last_name', e.target.value)}
                              />
                              {errors.guests?.[index]?.last_name && (
                                <div className='invalid-feedback'>
                                  {errors.guests[index].last_name}
                                </div>
                              )}
                            </div>

                            <div className='col-auto mt-11'>
                              <label className='form-label fw-bold invisible'>#</label>
                              <button
                                type='button'
                                className='btn btn-sm btn-icon btn-light-danger'
                                title={t('BUTTONS.DELETE')}
                                onClick={() => removeGuest(index)}
                              >
                                <span className='fa fa-trash' />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Questions */}
            {event.questions?.length > 0 && (
              <>
                <div className='separator mb-7' />
                <div className='mb-4'>
                  <div className='d-flex align-items-center mb-5'>
                    <div className='symbol symbol-35px mx-3'>
                      <span className='symbol-label bg-light-warning'>
                        <span className='fas fa-circle-question text-warning' />
                      </span>
                    </div>
                    <div>
                      <h4 className='fw-bolder mb-0'>{t('EVENT.QUESTIONS')}</h4>
                      <span className='text-muted fs-7'>
                        {event.questions.length} {t('EVENT.QUESTIONS')}
                      </span>
                    </div>
                  </div>

                  {event.questions.map((question: any, index: number) => (
                    <div
                      key={question.id}
                      className={`border rounded p-4 mb-4 ${
                        errors.questions?.[question.id] ? 'border-danger' : ''
                      }`}
                    >
                      <label
                        className={`form-label fw-bolder ${question.required ? 'required' : ''}`}
                      >
                        {index + 1}. {question.question}
                      </label>

                      {question.type === 'input' && (
                        <>
                          <input
                            type='text'
                            className={`form-control form-control-sm ${
                              errors.questions?.[question.id] ? 'is-invalid' : ''
                            }`}
                            value={(answers[question.id] as string) ?? ''}
                            onChange={(e) => handleInputAnswer(question.id, e.target.value)}
                          />

                          {errors.questions?.[question.id] && (
                            <div className='invalid-feedback'>{errors.questions[question.id]}</div>
                          )}
                        </>
                      )}

                      {question.type === 'radio' && (
                        <>
                          <div className='d-flex flex-wrap gap-5'>
                            {question.options?.map((option: any) => (
                              <div
                                className='form-check form-check-custom form-check-solid'
                                key={option.id}
                              >
                                <input
                                  className='form-check-input m-0'
                                  type='radio'
                                  name={`question_${question.id}`}
                                  id={`question_${question.id}_${option.id}`}
                                  checked={Number(answers[question.id]) === option.id}
                                  onChange={() => handleRadioAnswer(question.id, option.id)}
                                />

                                <label
                                  className='form-check-label mx-2 text-dark fw-bold'
                                  htmlFor={`question_${question.id}_${option.id}`}
                                >
                                  {option.option}
                                </label>
                              </div>
                            ))}
                          </div>

                          {errors.questions?.[question.id] && (
                            <div className='text-danger mt-2'>{errors.questions[question.id]}</div>
                          )}
                        </>
                      )}

                      {question.type === 'checkbox' && (
                        <>
                          <div className='d-flex flex-wrap gap-5'>
                            {question.options?.map((option: any) => (
                              <div
                                className='form-check form-check-custom form-check-solid'
                                key={option.id}
                              >
                                <input
                                  className='form-check-input m-0'
                                  type='checkbox'
                                  id={`question_${question.id}_${option.id}`}
                                  checked={
                                    Array.isArray(answers[question.id]) &&
                                    (answers[question.id] as number[]).includes(option.id)
                                  }
                                  onChange={(e) =>
                                    handleCheckboxAnswer(question.id, option.id, e.target.checked)
                                  }
                                />
                                <label
                                  className='form-check-label mx-2 text-dark fw-bold'
                                  htmlFor={`question_${question.id}_${option.id}`}
                                >
                                  {option.option}
                                </label>
                              </div>
                            ))}
                          </div>

                          {errors.questions?.[question.id] && (
                            <div className='text-danger fs-8 mt-2'>
                              {errors.questions[question.id]}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className='separator mb-7' />

            <div className='mb-4'>
              <div className='d-flex align-items-center mb-4'>
                <div className='symbol symbol-35px mx-3'>
                  <span className='symbol-label bg-light-primary'>
                    <span className='fa fa-comment text-primary' />
                  </span>
                </div>

                <div>
                  <h5 className='fw-bolder mb-0'>{t('EVENT.COMMENTS')}</h5>
                </div>
              </div>

              <textarea
                className='form-control'
                rows={4}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder={t('EVENT.COMMENTS')}
              />
            </div>
          </div>

          <div className='card-footer bg-light py-4'>
            <div className='d-flex justify-content-end'>
              <button
                type='submit'
                className='btn btn-sm btn-primary px-6 fw-bold'
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm me-2'
                      role='status'
                      aria-hidden='true'
                    />
                    {t('GLOBAL.PLEASE_WAIT')}
                  </>
                ) : (
                  <>
                    <span className='fa fa-paper-plane mx-2' />
                    {t('EVENT.SUBMIT')}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EventParticipation
